import { AxiosResponse } from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import Select from "react-select";
import { toast } from "react-toastify";
import { AddSensitivityResultData, DeleteSummary, GetResultSummary, MicrobiologyReportView, SaveResultSummary, UpdateLisStatus } from "Services/MicrobiologyResultData";
import { Loader } from "Shared/Common/Loader";
import useLang from "Shared/hooks/useLanguage";
import {
  reactSelectSMStyle2,
  styles
} from "../../../Utils/Common";
import PermissionComponent, {
  getPermissionDisplayName,
} from "../../../Shared/Common/Permissions/PermissionComponent";
import RejectModal from "./Modal/RejectModal";
import DeleteSensitivityModal from "./Modal/DeleteSensitivityModal";
import ViewHistoricalDataModal from "./Modal/ViewHistoricalDataModal";
import { savePdfUrls } from "../../../Redux/Actions/Index";
import { useMicrobiologyResultDataContext } from "./ResultDataContext";
import SensitivityDataTable from "./SensitivityDataTable";
import { NormalizedData, getActiveTabSortOrder, getCompleteLabelBySortOrder } from "./rowDataUtils";
export interface ReferenceLab {
  value: number;
  label: string;
}

interface ExpandableI {
  requisitionOrderId: number;
  requisitionId?: number;
  facilityId?: number;
}

export interface OptionType {
  value: number;
  label: string;
}

// The only LIS status from which Publish & Validate is allowed; also blocks Prelim,
// since the order is already finalized for reporting by then.
const READY_TO_PUBLISH_LIS_STATUS = "ready to publish";
// Sort order of the Completed tab, which holds finalized orders.
const COMPLETED_TAB_SORT_ORDER = 5;

const NO_SENSitizer_BUTTON_COLOR = "#0077b5";
const ADD_NEW_ANTIMICROBIAL_BUTTON_COLOR = "#6f42c1";
const MICROBIOLOGY_REQ_TYPE = 57;
const SENSITIVITY_PANEL_BUTTON_COLORS = [
  "#28a745",
  "#007bff",
  "#fd7e14",
  "#20c997",
  "#e83e8c",
  "#17a2b8",
  "#6c757d",
  "#dc3545",
];

const toNumber = (value: any): number => {
  const num = Number(value);
  return Number.isNaN(num) ? 0 : num;
};

const mapOrderPlateDescriptions = (items: any[] = []) =>
  items.map((pd) => ({
    id: toNumber(pd.id),
    descId: toNumber(pd.descId ?? pd.descLISId),
    label: pd.label ?? "",
    value: pd.value ?? pd.description ?? "",
  }));

const getSummaryPanelInfo = (summary: any) => {
  const panel = summary?.panels?.[0] ?? summary;
  return {
    panelId: toNumber(summary.panelId ?? panel?.id ?? panel?.value ?? panel?.panelId),
    panelName: summary.panelName ?? panel?.panelName ?? panel?.label ?? "",
    panelCode: summary.panelCode ?? panel?.panelCode ?? "",
  };
};

const createSensitivityInstanceKey = (panelData: any) => {
  const id = toNumber(panelData?.id);
  const suffix = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  if (id > 0) return `sensitivity-${id}-${suffix}`;
  return `client-${suffix}`;
};

const createSavedSensitivityInstanceKey = (panelData: any) => {
  const id = toNumber(panelData?.id);
  return id > 0 ? `saved-sensitivity-${id}` : createSensitivityInstanceKey(panelData);
};

const getPanelSensitivityRows = (panelData: any) => {
  const rows = NormalizedData(panelData, "sensitiResultData");
  return Array.isArray(rows) ? rows : [];
};

const mapSensitivityRows = (panelData: any) =>
  getPanelSensitivityRows(panelData).map((row: any) => ({
    id: toNumber(row.id),
    testConfigId: toNumber(NormalizedData(row, "testConfigId")),
    antimicrobial: NormalizedData(row, "antimicrobial") ?? "",
    mic: NormalizedData(row, "mic") ?? "",
    interpretation: NormalizedData(row, "interpretation") ?? "",
    isSelected: NormalizedData(row, "isSelected") ?? false,
  }));

const normalizeSensitivityPanel = (
  panelData: any,
  fallbackPanelId?: number,
  fallbackPanelName?: string,
  isRemovable = false,
  useStableKey = false
) => ({
  instanceKey: useStableKey
    ? createSavedSensitivityInstanceKey(panelData)
    : createSensitivityInstanceKey(panelData),
  id: toNumber(panelData?.id),
  panelId: toNumber(panelData?.panelId ?? fallbackPanelId),
  panelName: panelData?.panelName ?? fallbackPanelName ?? "",
  organismId: panelData?.organismId ?? null,
  cannedCommentId: panelData?.cannedCommentId ?? null,
  isRemovable,
  sensitiResultData: mapSensitivityRows(panelData),
});

const applySensitivityRemovableRules = (panels: any[]) =>
  panels.map((panel, index) => ({
    ...panel,
    isRemovable: index !== 0,
  }));

const normalizeSensitivityPanelsFromSummary = (panels: any[]) =>
  applySensitivityRemovableRules(
    panels.map((panel) =>
      normalizeSensitivityPanel(
        panel,
        toNumber(panel.panelId),
        panel.panelName,
        false,
        true
      )
    )
  );

const isSensitivityRowData = (item: any) =>
  NormalizedData(item, "antimicrobial") !== undefined &&
  getPanelSensitivityRows(item).length === 0;

const parseAddSensitivityResponse = (
  responseData: any,
  panelId: number,
  panelName: string,
  isRemovable = false
) => {
  const nestedPanels = NormalizedData(responseData, "sentivityResultData");
  if (Array.isArray(nestedPanels) && nestedPanels.length > 0) {
    const targetPanel = isRemovable
      ? nestedPanels[nestedPanels.length - 1]
      : nestedPanels.find((item) => toNumber(item?.panelId) === panelId) ?? nestedPanels[0];
    return normalizeSensitivityPanel(
      targetPanel,
      toNumber(NormalizedData(targetPanel, "panelId")) || panelId,
      NormalizedData(targetPanel, "panelName") || panelName,
      isRemovable
    );
  }

  if (Array.isArray(responseData)) {
    if (responseData.length > 0 && responseData.every(isSensitivityRowData)) {
      return normalizeSensitivityPanel(
        { panelId, panelName, sensitiResultData: responseData },
        panelId,
        panelName,
        isRemovable
      );
    }
    // Add New Antimicrobial returns every panel with the same panelId — take the last (newest).
    const matched = isRemovable
      ? responseData[responseData.length - 1]
      : responseData.find((item) => toNumber(item?.panelId) === panelId) ??
      responseData[responseData.length - 1];
    return normalizeSensitivityPanel(matched, panelId, panelName, isRemovable);
  }

  return normalizeSensitivityPanel(responseData, panelId, panelName, isRemovable);
};

interface LoadResultSummaryOptions {
  mergeSensitivityPanel?: ReturnType<typeof parseAddSensitivityResponse>;
  previousSensitivityCount?: number;
}

const buildSaveResultSummaryPayload = (
  requisitionOrderId: number,
  tabId: number,
  summaries: any[],
  sentivityResultData: any[]
) => ({
  requsitionOrderId: requisitionOrderId,
  tabId,
  orderResultInfoDatas: summaries.map((s) => {
    const panel = getSummaryPanelInfo(s);
    return {
      id: toNumber(s.id),
      panelId: panel.panelId,
      panelName: panel.panelName,
      colonyId: toNumber(s.colonyId),
      cultureGrowthId: toNumber(s.cultureGrowthId),
      gramStainId: toNumber(s.gramStainId),
      sourceId: toNumber(s.sourceId),
      internalComments: s.internalComments || "",
      significantFinding: s.significantFinding || "",
      plateDescriptions: mapOrderPlateDescriptions(s.plateDescriptions),
    };
  }),
  sentivityResultData: (sentivityResultData || []).map((panel) => ({
    id: toNumber(panel.id),
    panelId: toNumber(panel.panelId),
    panelName: panel.panelName || "",
    organismId: toNumber(panel.organismId),
    cannedCommentId: toNumber(panel.cannedCommentId),
    sensitiResultData: (panel.sensitiResultData || []).map((row: any) => ({
      id: toNumber(row.id),
      testConfigId: toNumber(row.testConfigId),
      antimicrobial: row.antimicrobial ?? "",
      mic: row.mic ?? "",
      interpretation: row.interpretation ?? "",
      isSelected: row.isSelected ?? false,
    })),
  })),
});

const validateSelectedSensitivityRows = (panels: any[]) => {
  for (const panel of panels) {
    for (const row of panel.sensitiResultData || []) {
      if (!row.isSelected) continue;
      const antimicrobial = row.antimicrobial || "Antimicrobial";
      if (!String(row.mic ?? "").trim()) {
        return `${antimicrobial}: MIC is required when selected.`;
      }
      if (!String(row.interpretation ?? "").trim()) {
        return `${antimicrobial}: Interpretation is required when selected.`;
      }
    }
  }
  return null;
};

const validateResultSummary = (summaryItems: any[]) => {
  for (const summary of summaryItems) {
    const panelName = summary.panelName || "Panel";
    if (!toNumber(summary.colonyId)) {
      return `${panelName}: Colony Count is required.`;
    }
    if (!toNumber(summary.cultureGrowthId)) {
      return `${panelName}: Culture Growth is required.`;
    }
  }
  return null;
};

const hasSummaryResultValue = (summary: any) => {
  if (
    toNumber(summary.colonyId) ||
    toNumber(summary.cultureGrowthId) ||
    toNumber(summary.gramStainId) ||
    toNumber(summary.sourceId)
  ) {
    return true;
  }
  if (String(summary.internalComments ?? "").trim()) return true;
  if (String(summary.significantFinding ?? "").trim()) return true;
  return (summary.plateDescriptions || []).some(
    (pd: any) => String(pd.description ?? pd.value ?? "").trim()
  );
};

const hasSensitivityResultValue = (panels: any[]) =>
  (panels || []).some((panel) => {
    if (toNumber(panel.organismId) || toNumber(panel.cannedCommentId)) {
      return true;
    }
    return (panel.sensitiResultData || []).some(
      (row: any) =>
        row.isSelected ||
        String(row.mic ?? "").trim() ||
        String(row.interpretation ?? "").trim()
    );
  });

const areAllResultFieldsBlank = (summaryItems: any[], panels: any[]) =>
  !(
    (summaryItems || []).some(hasSummaryResultValue) ||
    hasSensitivityResultValue(panels)
  );

const createDefaultPlateDescription = () => ({
  descLISId: 0,
  descId: 0,
  id: 0,
  label: "",
  description: "",
  value: "",
});

const normalizePlateDescriptionItem = (pd: any) => ({
  id: toNumber(pd.id),
  descId: toNumber(pd.descId ?? pd.descLISId),
  descLISId: toNumber(pd.descLISId ?? pd.descId),
  label: pd.label ?? "",
  description: pd.description ?? pd.value ?? "",
  value: pd.value ?? pd.description ?? "",
});

const ensureSummaryPlateDescription = (summary: any) => ({
  ...summary,
  plateDescriptions:
    Array.isArray(summary.plateDescriptions) && summary.plateDescriptions.length > 0
      ? summary.plateDescriptions.map(normalizePlateDescriptionItem)
      : [],
});

const createEmptySummary = (
  requisitionOrderId: number,
  tabId: number,
  panel?: { panelId?: number; panelName?: string; panelCode?: string }
) => ({
  id: 0,
  requsitionOrderId: requisitionOrderId,
  panelId: panel?.panelId ?? 0,
  panelName: panel?.panelName ?? "",
  panelCode: panel?.panelCode ?? "",
  colonyId: null,
  cultureGrowthId: null,
  gramStainId: null,
  sourceId: null,
  organismId: 0,
  cannedCommentId: 0,
  internalComments: "",
  significantFinding: "",
  tabId: tabId || 0,
  plateDescriptions: [createDefaultPlateDescription()],
});

const LookupSelect = ({
  label,
  inputId,
  options,
  value,
  onChange,
  required = false,
  showValidation = false,
}: {
  label: string;
  inputId: string;
  options: any[];
  value: any;
  onChange: (val: any) => void;
  required?: boolean;
  showValidation?: boolean;
}) => {
  const { t } = useLang();
  const isInvalid = showValidation && required && !toNumber(value);
  return (
    <div className={`w-100${isInvalid ? " is-invalid" : ""}`}>
      <span>
        {label}
        {required && <span className="text-danger"> *</span>}
      </span>
      <Select
        inputId={inputId}
        menuPortalTarget={document.body}
        styles={reactSelectSMStyle2()}
        theme={(theme: any) => styles(theme)}
        placeholder={t("--- Select ---")}
        isSearchable
        isClearable
        options={options}
        value={options.find((option) => option.value == value) ?? null}
        onChange={(selectedOption: any) => onChange(selectedOption?.value ?? null)}
      />
    </div>
  );
};

const ResultDataExpandableRow = (props: ExpandableI) => {
  const { t } = useLang();
  const dispatch = useDispatch();
  const { requisitionOrderId, requisitionId, facilityId: facilityIdProp } = props;
  const {
    filterData,
    loadGridData,
    data,
    value,
    rowsToExpand,
    setRowsToExpand,
    isMasterExpandTriggered,
  } = useMicrobiologyResultDataContext();

  const activeSortOrder = getActiveTabSortOrder(data.gridHeaders, value);
  const isCompletedTab = activeSortOrder === COMPLETED_TAB_SORT_ORDER;

  const targetRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUpdatingLisStatus, setIsUpdatingLisStatus] = useState(false);
  const [updatingLisStatusAction, setUpdatingLisStatusAction] = useState<"publish" | "complete" | null>(null);
  const [isNoSensitizerSaving, setIsNoSensitizerSaving] = useState(false);
  const [colonyCountOptions, setColonyCountOptions] = useState<any[]>([]);
  const [cultureGrowthOptions, setCultureGrowthOptions] = useState<any[]>([]);
  const [gramStainOptions, setGramStainOptions] = useState<any[]>([]);
  const [sourceOptions, setSourceOptions] = useState<any[]>([]);
  const [organismOptions, setOrganismOptions] = useState<any[]>([]);
  const [cannedCommentOptions, setCannedCommentOptions] = useState<any[]>([]);
  const [summaries, setSummaries] = useState<any[]>([]);
  const [sensitivityButtonData, setSensitivityButtonData] = useState<any[]>([]);
  const [showViewHistoricalData, setShowViewHistoricalData] = useState(false);
  const [isShowHistoricData, setIsShowHistoricData] = useState(false);
  const [isShowCompleteButton, setIsShowCompleteButton] = useState(false);
  const [sentivityResultData, setSentivityResultData] = useState<any[]>([]);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [isLoadingSensitivity, setIsLoadingSensitivity] = useState(false);
  const [showSensitivityValidation, setShowSensitivityValidation] = useState(false);
  const [showSummaryValidation, setShowSummaryValidation] = useState(false);
  const [panelToDelete, setPanelToDelete] = useState<any | null>(null);
  const [isDeletingSensitivity, setIsDeletingSensitivity] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isApplyingPrelim, setIsApplyingPrelim] = useState(false);
  const [reportMeta, setReportMeta] = useState({
    facilityId: 0,
    reqType: MICROBIOLOGY_REQ_TYPE,
    requisitionId: 0,
  });

  const [plateData, setPlateData] = useState<any>({
    selectedOrganism: "",
    colonyCount: "",
    bap: "",
    mac: "",
    internalComments: "",
    significantFindings: "",
    plateDescriptions: [],
  });

  const updatePlateDescription = (summaryIndex: number, plateDescIndex: number, text: string) => {
    setSummaries((prev) => {
      const copy = [...prev];
      const existing = ensureSummaryPlateDescription(copy[summaryIndex] || {});
      const plateDescriptions = existing.plateDescriptions.map((pd: any, i: number) =>
        i === plateDescIndex ? { ...pd, description: text, value: text } : pd
      );
      copy[summaryIndex] = { ...existing, plateDescriptions };
      return copy;
    });
  };

  const loadResultSummary = useCallback(async (
    showLoading = true,
    options?: LoadResultSummaryOptions
  ) => {
    if (showLoading) setLoading(true);

    try {
      const summaryRes: AxiosResponse = await GetResultSummary({
        requisitionOrderId: requisitionOrderId,
        tabId: filterData?.tabId ?? 0,
      });
      const raw = summaryRes?.data;
      const responseData = raw?.data ?? raw;
      const lookUpData = responseData?.lookUpData;

      setReportMeta({
        facilityId: toNumber(
          NormalizedData(responseData, "facilityId") ??
          NormalizedData(responseData, "facilityID")
        ),
        reqType:
          toNumber(
            NormalizedData(responseData, "reqType") ??
            NormalizedData(responseData, "reqTypeId")
          ) || MICROBIOLOGY_REQ_TYPE,
        requisitionId: toNumber(
          NormalizedData(responseData, "requisitionId") ??
          NormalizedData(responseData, "requisitionID")
        ),
      });

      if (Array.isArray(lookUpData?.colonyCounts)) {
        setColonyCountOptions(lookUpData.colonyCounts);
      }
      if (Array.isArray(lookUpData?.cultureGrowths)) {
        setCultureGrowthOptions(lookUpData.cultureGrowths);
      }
      if (Array.isArray(lookUpData?.gramStains)) {
        setGramStainOptions(lookUpData.gramStains);
      }
      if (Array.isArray(lookUpData?.sources)) {
        setSourceOptions(lookUpData.sources);
      }
      if (Array.isArray(lookUpData?.organisms)) {
        setOrganismOptions(lookUpData.organisms);
      }
      if (Array.isArray(lookUpData?.cannedComments)) {
        setCannedCommentOptions(lookUpData.cannedComments);
      }

      setIsShowHistoricData(!!responseData?.isShowHistoricData);
      setIsShowCompleteButton(!!responseData?.isShowCompleteButton);
      if (!responseData?.isShowHistoricData) {
        setShowViewHistoricalData(false);
      }

      const summaryArray = Array.isArray(responseData?.orderResultInfoDatas)
        ? responseData.orderResultInfoDatas
        : [];

      const panelButtons = Array.isArray(responseData?.sentivityButtonData)
        ? responseData.sentivityButtonData
        : [];

      setSensitivityButtonData(panelButtons);

      const sensitivityPanels = Array.isArray(responseData?.sentivityResultData)
        ? responseData.sentivityResultData
        : [];

      if (sensitivityPanels.length > 0) {
        let panels = normalizeSensitivityPanelsFromSummary(sensitivityPanels);
        const mergedPanel = options?.mergeSensitivityPanel;
        const hasMergedRows = (mergedPanel?.sensitiResultData?.length ?? 0) > 0;

        if (hasMergedRows && mergedPanel) {
          const mergedId = toNumber(mergedPanel.id);
          const mergedPanelId = toNumber(mergedPanel.panelId);

          if (mergedPanel.isRemovable) {
            // Match by record id first. Otherwise, if summary already grew, the new
            // panel is at the end — fill missing rows instead of appending a duplicate.
            let existingIndex =
              mergedId > 0
                ? panels.findIndex((panel) => toNumber(panel.id) === mergedId)
                : -1;

            if (
              existingIndex < 0 &&
              typeof options?.previousSensitivityCount === "number" &&
              panels.length > options.previousSensitivityCount
            ) {
              existingIndex = panels.length - 1;
            }

            if (existingIndex >= 0) {
              panels = panels.map((panel, index) =>
                index === existingIndex
                  ? {
                    ...panel,
                    instanceKey: panel.instanceKey,
                    sensitiResultData:
                      (panel.sensitiResultData?.length ?? 0) > 0
                        ? panel.sensitiResultData
                        : mergedPanel.sensitiResultData,
                  }
                  : panel
              );
            } else {
              panels = applySensitivityRemovableRules([...panels, mergedPanel]);
            }
          } else {
            const existingIndex = panels.findIndex(
              (panel) => toNumber(panel.panelId) === mergedPanelId
            );
            if (existingIndex >= 0) {
              panels = panels.map((panel, index) =>
                index === existingIndex
                  ? {
                    ...panel,
                    ...mergedPanel,
                    instanceKey: panel.instanceKey,
                    sensitiResultData:
                      mergedPanel.sensitiResultData?.length > 0
                        ? mergedPanel.sensitiResultData
                        : panel.sensitiResultData,
                  }
                  : panel
              );
            } else {
              panels = applySensitivityRemovableRules([...panels, mergedPanel]);
            }
          }
        }

        setSentivityResultData(panels);
      } else {
        const mergedPanel = options?.mergeSensitivityPanel;
        if ((mergedPanel?.sensitiResultData?.length ?? 0) > 0) {
          setSentivityResultData((prev) => {
            const mergedPanelId = toNumber(mergedPanel!.panelId);
            const mergedId = toNumber(mergedPanel!.id);

            if (mergedPanel!.isRemovable) {
              const alreadyPresent =
                mergedId > 0
                  ? prev.some((panel) => toNumber(panel.id) === mergedId)
                  : prev.some((panel) => panel.instanceKey === mergedPanel!.instanceKey);
              if (alreadyPresent) return prev;
              return applySensitivityRemovableRules([...prev, mergedPanel!]);
            }

            const existingIndex = prev.findIndex(
              (panel) => toNumber(panel.panelId) === mergedPanelId
            );

            if (existingIndex >= 0) {
              const copy = [...prev];
              copy[existingIndex] = {
                ...mergedPanel!,
                instanceKey: copy[existingIndex].instanceKey,
              };
              return applySensitivityRemovableRules(copy);
            }

            return applySensitivityRemovableRules([...prev, mergedPanel!]);
          });
        } else {
          setSentivityResultData([]);
        }
      }
      setShowSensitivityValidation(false);
      setShowSummaryValidation(false);

      if (summaryArray.length > 0) {
        setSummaries(summaryArray.map(ensureSummaryPlateDescription));
      } else {
        setSummaries([
          createEmptySummary(requisitionOrderId, filterData?.tabId ?? 0),
        ]);
      }
    } catch (err: any) {
      console.error("Error fetching result summary:", err);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [requisitionOrderId, filterData?.tabId]);

  const getCurrentSummaryItems = () =>
    summaries.length > 0
      ? summaries.map(ensureSummaryPlateDescription)
      : [createEmptySummary(requisitionOrderId, filterData?.tabId ?? 0)];

  // Mandatory results that must be complete before a report is generated:
  // Colony Count / Culture Growth per panel, and MIC / Interpretation on selected
  // sensitivity rows. Highlights the offending fields and returns false when incomplete.
  const validateMandatoryResults = (summaryItems: any[]) => {
    const summaryValidationError = validateResultSummary(summaryItems);
    if (summaryValidationError) {
      setShowSummaryValidation(true);
      toast.error(t(summaryValidationError));
      return false;
    }
    setShowSummaryValidation(false);

    const sensitivityValidationError = validateSelectedSensitivityRows(sentivityResultData);
    if (sensitivityValidationError) {
      setShowSensitivityValidation(true);
      toast.error(t(sensitivityValidationError));
      return false;
    }
    setShowSensitivityValidation(false);

    return true;
  };

  const saveMicrobiologyExpandResultData = async () => {
    if (isSubmitting) return;

    const summaryItems = getCurrentSummaryItems();

    if (!validateMandatoryResults(summaryItems)) return;

    setIsSubmitting(true);

    const summaryPayload = buildSaveResultSummaryPayload(
      requisitionOrderId,
      filterData?.tabId ?? 0,
      summaryItems,
      sentivityResultData
    );

    try {
      const saveRes: AxiosResponse = await SaveResultSummary(summaryPayload);
      if (saveRes?.data?.statusCode === 200) {
        toast.success(t(saveRes?.data?.message || "Result summary saved"));
        await loadResultSummary(false);
      } else {
        toast.error(t(saveRes?.data?.message || "Failed to save result summary"));
      }
    } catch (err: any) {
      console.trace("Error saving result summary:", err);
      toast.error(t("Error saving result summary"));
    } finally {
      setIsSubmitting(false);
      loadGridData(false);
    }
  }

  useEffect(() => {
    loadResultSummary();
  }, [loadResultSummary]);

  const updateSummaryField = (index: number, field: string, value: any) => {
    setSummaries((prev) => {
      const copy = [...prev];
      const existing = copy[index] || {};
      const updated = { ...existing };
      if (field === "colonyCount") {
        updated.colonyId = value;
      } else if (field === "significantFindings") {
        updated.significantFinding = value;
      } else {
        updated[field] = value;
      }
      copy[index] = updated;
      return copy;
    });
  };

  const updateSensitivityPanelField = (instanceKey: string, field: string, value: any) => {
    setSentivityResultData((prev) =>
      prev.map((panel) =>
        panel.instanceKey === instanceKey ? { ...panel, [field]: value } : panel
      )
    );
  };

  const updateSensitivityRow = (
    instanceKey: string,
    rowId: number,
    field: string,
    value: any
  ) => {
    setSentivityResultData((prev) =>
      prev.map((panel) =>
        panel.instanceKey === instanceKey
          ? {
            ...panel,
            sensitiResultData: panel.sensitiResultData.map((row: any) =>
              toNumber(row.id) === rowId ? { ...row, [field]: value } : row
            ),
          }
          : panel
      )
    );
  };

  const updateSensitivitySelectAll = (instanceKey: string, isSelected: boolean) => {
    setSentivityResultData((prev) =>
      prev.map((panel) =>
        panel.instanceKey === instanceKey
          ? {
            ...panel,
            sensitiResultData: panel.sensitiResultData.map((row: any) => ({
              ...row,
              isSelected,
            })),
          }
          : panel
      )
    );
  };

  const removeSensitivityPanel = (instanceKey: string) => {
    setSentivityResultData((prev) =>
      applySensitivityRemovableRules(prev.filter((panel) => panel.instanceKey !== instanceKey))
    );
  };

  const handleRequestDeleteSensitivity = (panel: any) => {
    if (!panel) return;
    setPanelToDelete(panel);
  };

  const handleConfirmDeleteSensitivity = async () => {
    if (!panelToDelete) return;

    const summaryId = toNumber(panelToDelete.id);
    const instanceKey = panelToDelete.instanceKey;

    if (summaryId <= 0) {
      removeSensitivityPanel(instanceKey);
      setPanelToDelete(null);
      return;
    }

    setIsDeletingSensitivity(true);
    try {
      const response: AxiosResponse = await DeleteSummary(summaryId);
      if (response?.data?.statusCode === 200) {
        toast.success(t(response?.data?.message || "Antimicrobial panel deleted successfully"));
        removeSensitivityPanel(instanceKey);
        setPanelToDelete(null);
      } else {
        toast.error(t(response?.data?.message || "Failed to delete antimicrobial panel"));
      }
    } catch (error) {
      console.error("Error deleting sensitivity panel:", error);
      toast.error(t("Failed to delete antimicrobial panel"));
    } finally {
      setIsDeletingSensitivity(false);
    }
  };

  const loadSensitivityPanel = async (
    panelId: number,
    panelName: string,
    isRemovable = false
  ) => {
    if (isLoadingSensitivity) return;

    setIsLoadingSensitivity(true);
    try {
      const response = await AddSensitivityResultData({
        requsitionOrderId: requisitionOrderId,
        panelId,
        tabId: filterData?.tabId ?? 0,
      });

      if (response?.data?.statusCode !== 200) {
        toast.error(t(response?.data?.message || "Failed to load sensitivity data"));
        return;
      };

      await loadResultSummary(false);
    } catch (error) {
      console.error("Error loading sensitivity data:", error);
      toast.error(t("Failed to load sensitivity data"));
    } finally {
      setIsLoadingSensitivity(false);
    }
  };

  const getGridRow = () =>
    data?.gridData?.find(
      (item) => toNumber(NormalizedData(item, "requisitionorderid")) === requisitionOrderId
    );

  const getCurrentLisStatus = () =>
    String(NormalizedData(getGridRow(), "lisstatus") ?? "").toLowerCase().trim();

  // Recomputed on every render so it tracks the grid row refreshed by loadGridData.
  const isReadyToPublish = getCurrentLisStatus() === READY_TO_PUBLISH_LIS_STATUS;
  // Publishing requires "Ready to Publish", except on the Completed tab, where the
  // button is a Re-Publish & Validate of an already-finalized order.
  const canPublishAndValidate = isCompletedTab || isReadyToPublish;

  const buildMicrobiologyReportPayload = (isPreview: boolean) => {
    const gridRow = getGridRow();

    return {
      requisitionId: toNumber(
        requisitionId ??
        reportMeta.requisitionId ??
        NormalizedData(gridRow, "requisitionid")
      ),
      requisitionOrderId: toNumber(requisitionOrderId),
      reqType:
        toNumber(reportMeta.reqType) ||
        toNumber(NormalizedData(gridRow, "reqtype")) ||
        MICROBIOLOGY_REQ_TYPE,
      facilityId: toNumber(
        facilityIdProp ??
        reportMeta.facilityId ??
        NormalizedData(gridRow, "facilityid")
      ),
      tabId: toNumber(filterData?.tabId),
      isPreview,
      isCorrectedReport: false,
      isAmendedReport: false,
    };
  };

  const callMicrobiologyReport = async (isPreview: boolean) => {
    if (!requisitionOrderId) {
      toast.error(t("Requisition order ID is required"));
      return;
    }

    const payload = buildMicrobiologyReportPayload(isPreview);

    if (!payload.requisitionId) {
      toast.error(t("Requisition ID is required"));
      return;
    }

    const res: AxiosResponse = await MicrobiologyReportView(payload);
    if (res?.data?.statusCode === 200) {
      const defaultMessage = isPreview
        ? "Preview generated successfully"
        : activeSortOrder === 5
          ? "Re-published and validated"
          : "Published and validated";
      toast.success(t(res?.data?.message || defaultMessage));
      if (isPreview) {
        dispatch(savePdfUrls(res?.data?.data));
        window.open("/docs-viewer", "_blank", "noreferrer");
      }
      loadGridData(false);
    } else {
      const defaultError = isPreview
        ? "Preview is not available"
        : activeSortOrder === 5
          ? "Failed to re-publish and validate"
          : "Failed to publish and validate";
      toast.error(t(res?.data?.message || defaultError));
    }
  };

  const handlePublishAndValidate = async () => {
    // The button is disabled outside "Ready to Publish"; this guards a stale grid row.
    if (!canPublishAndValidate) {
      toast.error(t("Publish & Validate is only allowed when the status is Ready to Publish."));
      return;
    }
    if (!requisitionId) {
      toast.error(t("Requisition ID is required"));
      return;
    }
    // No final report / validation until every mandatory result is complete.
    if (!validateMandatoryResults(getCurrentSummaryItems())) return;
    setIsUpdatingLisStatus(true);
    setUpdatingLisStatusAction("publish");
    try {
      await callMicrobiologyReport(false);
    } catch (err: any) {
      toast.error(t(err?.response?.data?.message || (activeSortOrder === 5 ? "Error re-publishing and validating" : "Error publishing and validating")));
    } finally {
      setIsUpdatingLisStatus(false);
      setUpdatingLisStatusAction(null);
    }
  };

  const handleNoSensitizerRequired = async () => {
    // Also generates the final report, so it needs the same mandatory-result check.
    if (!validateMandatoryResults(getCurrentSummaryItems())) return;
    setIsNoSensitizerSaving(true);
    try {
      await callMicrobiologyReport(false);
    } catch (err: any) {
      toast.error(t(err?.response?.data?.message || (activeSortOrder === 5 ? "Error re-publishing and validating" : "Error publishing and validating")));
    } finally {
      setIsNoSensitizerSaving(false);
    }
  };

  const handleMicrobiologyPreview = async () => {
    setIsPreviewing(true);
    try {
      await callMicrobiologyReport(true);
    } catch (err: any) {
      console.error("Error generating microbiology preview:", err);
      toast.error(t(err?.response?.data?.message || "Preview is not available"));
    } finally {
      setIsPreviewing(false);
    }
  };

  const handlePrelim = async () => {
    if (isApplyingPrelim || isUpdatingLisStatus) return;

    if (isCompletedTab) {
      toast.error(t("Prelim is not allowed for a completed order."));
      return;
    }

    if (getCurrentLisStatus() === READY_TO_PUBLISH_LIS_STATUS) {
      toast.error(
        t("Prelim is not available once the result is 'Ready to Publish."));
      return;
    }

    if (!requisitionOrderId) {
      toast.error(t("Requisition order ID is required"));
      return;
    }

    const reportPayload = {
      ...buildMicrobiologyReportPayload(false),
      isPrelim: true,
    };

    if (!reportPayload.requisitionId) {
      toast.error(t("Requisition ID is required"));
      return;
    }

    const summaryItems = getCurrentSummaryItems();

    if (areAllResultFieldsBlank(summaryItems, sentivityResultData)) {
      setShowSummaryValidation(true);
      toast.error(t("Please enter results before applying Prelim."));
      return;
    }
    setShowSummaryValidation(false);

    setIsApplyingPrelim(true);
    try {
      const saveRes: AxiosResponse = await SaveResultSummary({
        ...buildSaveResultSummaryPayload(
          requisitionOrderId,
          filterData?.tabId ?? 0,
          summaryItems,
          sentivityResultData
        ),
        isPrelim: true,
      });

      if (saveRes?.data?.statusCode !== 200) {
        toast.error(t(saveRes?.data?.message || "Failed to save result summary"));
        return;
      }

      const reportRes: AxiosResponse = await MicrobiologyReportView(reportPayload);
      if (reportRes?.data?.statusCode === 200) {
        toast.success(t(reportRes?.data?.message || saveRes?.data?.message || "Prelim applied successfully"));
        await loadResultSummary(false);
        loadGridData(false);
      } else {
        toast.error(t(reportRes?.data?.message || "Failed to apply prelim"));
      }
    } catch (err: any) {
      toast.error(t(err?.response?.data?.message || "Error applying prelim"));
    } finally {
      setIsApplyingPrelim(false);
    }
  };

  return (
    <div
      ref={
        rowsToExpand.length > 0 &&
          requisitionOrderId === rowsToExpand[rowsToExpand.length - 2]
          ? targetRef
          : null
      }
      className="d-flex flex-column flex-column-fluid table-expend-sticky table-expend-sm-sticky"
    >
      <div id="kt_app_content" className="app-content flex-column-fluid pb-0">
        {loading ? (
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
            <Loader />
          </div>
        ) : (
          <div
            id="kt_app_content_container"
            className="app-container container-fluid mb-container"
          >
            <h6 className="text-primary h5">{t("Results Summary")}</h6>
            {summaries.map((s: any, idx: number) => {
              const plateDescriptions = ensureSummaryPlateDescription(s).plateDescriptions;

              return (
                <div key={s.id || idx} className="card rounded mb-4" style={{ border: "2px solid orange" }}>
                  <div className="card-header px-4 d-flex justify-content-between align-items-center rounded bg-light-warning min-h-40px">
                    <h6 className="h5 mb-0">{s.panelName}</h6>
                  </div>
                  <div className="card-body py-md-4 py-2 px-4">
                    <div className="row g-3">
                      <div className="col-xl-4 col-md-4 col-sm-12">
                        <LookupSelect
                          label={t("Colony Count")}
                          inputId={`ColonyCountSelect_${requisitionOrderId}_${idx}`}
                          options={colonyCountOptions}
                          value={s.colonyId}
                          onChange={(val) => updateSummaryField(idx, "colonyId", val)}
                          required
                          showValidation={showSummaryValidation}
                        />
                      </div>
                      <div className="col-xl-4 col-md-4 col-sm-12">
                        <LookupSelect
                          label={t("Culture Growth")}
                          inputId={`CultureGrowthSelect_${requisitionOrderId}_${idx}`}
                          options={cultureGrowthOptions}
                          value={s.cultureGrowthId}
                          onChange={(val) => updateSummaryField(idx, "cultureGrowthId", val)}
                          required
                          showValidation={showSummaryValidation}
                        />
                      </div>
                      <div className="col-xl-4 col-md-4 col-sm-12">
                        <LookupSelect
                          label={t("Gram Stain")}
                          inputId={`GramStainSelect_${requisitionOrderId}_${idx}`}
                          options={gramStainOptions}
                          value={s.gramStainId}
                          onChange={(val) => updateSummaryField(idx, "gramStainId", val)}
                        />
                      </div>
                      <div className="col-xl-4 col-md-4 col-sm-12">
                        <LookupSelect
                          label={t("Source")}
                          inputId={`SourceSelect_${requisitionOrderId}_${idx}`}
                          options={sourceOptions}
                          value={s.sourceId}
                          onChange={(val) => updateSummaryField(idx, "sourceId", val)}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="px-4">
                    <h6 className="text-primary h5 mb-3">
                      {t("Plate Description")}
                    </h6>
                    <div className="row g-3 flex-wrap">
                      {plateDescriptions.map((pd: any, pdIdx: number) => (
                        <div
                          key={pd.descId || pd.id || pdIdx}
                          className="col-xl-6 col-md-12 col-sm-12"
                        >
                          <div className="w-100">
                            <span>{pd.label}</span>
                            <input
                              id={`PlateDescInput_${requisitionOrderId}_${idx}_${pdIdx}`}
                              type="text"
                              className="form-control bg-white mb-3 mb-lg-0 rounded-2 h-30px fs-7 w-100"
                              placeholder={t("-- Type --")}
                              value={pd.description ?? pd.value ?? ""}
                              onChange={(e) => updatePlateDescription(idx, pdIdx, e.target.value)}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="row g-3 mt-2 mb-2">
                      <div className="col-md-6 col-12">
                        <span>{t("Internal Comments:")}</span>
                        <textarea
                          id={`CategorySetupAllTestListSearch_InternalComments_${requisitionOrderId}_${idx}`}
                          className="form-control bg-white mb-3 mb-lg-0 rounded-2 h-70px fs-7 w-100"
                          rows={5}
                          placeholder={t("Textarea")}
                          value={s.internalComments || ""}
                          onChange={(e) => updateSummaryField(idx, "internalComments", e.target.value)}
                        />
                      </div>
                      <div className="col-md-6 col-12">
                        <span>{t("Significant Findings:")}</span>
                        <textarea
                          id={`CategorySetupAllTestListSearch_SignificantFindings_${requisitionOrderId}_${idx}`}
                          className="form-control bg-white mb-3 mb-lg-0 rounded-2 h-70px fs-7 w-100"
                          rows={5}
                          placeholder={t("Textarea")}
                          value={s.significantFinding || ""}
                          onChange={(e) => updateSummaryField(idx, "significantFindings", e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {sentivityResultData.map((panel: any) => (
              <div
                key={panel.instanceKey}
                className="card rounded mb-4 position-relative"
                style={{ border: "1px solid #dee2e6" }}
              >
                <button
                  type="button"
                  className="btn btn-sm btn-icon btn-light-danger position-absolute top-0 end-0 m-2 rounded-circle"
                  style={{ width: "28px", height: "28px", zIndex: 1 }}
                  title={t("Remove")}
                  aria-label={t("Remove sensitivity panel")}
                  onClick={() => handleRequestDeleteSensitivity(panel)}
                >
                  <i className="bi bi-x-lg fs-8" />
                </button>
                <div className="card-header px-4 bg-light min-h-40px d-flex align-items-center">
                  <h6 className="h6 mb-0 pe-5">{panel.panelName}</h6>
                </div>
                <div className="card-body px-4 pb-4">
                  <div className="row g-3 mb-3">
                    <div className="col-md-6 col-12">
                      <LookupSelect
                        label={t("Organism Name")}
                        inputId={`SensitivityOrganism_${requisitionOrderId}_${panel.instanceKey}`}
                        options={organismOptions}
                        value={panel.organismId}
                        onChange={(val) =>
                          updateSensitivityPanelField(panel.instanceKey, "organismId", val)
                        }
                      />
                    </div>
                    <div className="col-md-6 col-12">
                      <LookupSelect
                        label={t("Canned Comments")}
                        inputId={`SensitivityCannedComments_${requisitionOrderId}_${panel.instanceKey}`}
                        options={cannedCommentOptions}
                        value={panel.cannedCommentId}
                        onChange={(val) =>
                          updateSensitivityPanelField(panel.instanceKey, "cannedCommentId", val)
                        }
                      />
                    </div>
                  </div>
                  <SensitivityDataTable
                    rows={panel.sensitiResultData}
                    showValidation={showSensitivityValidation}
                    onRowChange={(rowId, field, value) =>
                      updateSensitivityRow(panel.instanceKey, rowId, field, value)
                    }
                    onSelectAll={(isSelected) =>
                      updateSensitivitySelectAll(panel.instanceKey, isSelected)
                    }
                  />
                </div>
              </div>
            ))}

            {
              activeSortOrder === 2 || activeSortOrder === 5 ? null : (
                <>
                  <div className="d-flex flex-wrap align-items-center gap-2 px-4 mb-4 w-100">
                    {sensitivityButtonData.map((p: any, panelIdx: number) => (
                      <button
                        key={p.panelId || p.id || p.value || panelIdx}
                        type="button"
                        className="btn btn-sm fw-bold text-white"
                        style={{
                          background:
                            SENSITIVITY_PANEL_BUTTON_COLORS[
                            panelIdx % SENSITIVITY_PANEL_BUTTON_COLORS.length
                            ],
                        }}
                        onClick={async () => {
                          const panelId = toNumber(p.panelId ?? p.id ?? p.value);
                          const panelName = p.panelName || p.label || "";
                          await loadSensitivityPanel(panelId, panelName, false);
                        }}
                        disabled={isLoadingSensitivity}
                      >
                        {p.panelName || p.label}
                      </button>
                    ))}

                    <PermissionComponent
                      moduleName="Microbiology LIS"
                      pageName="Result Data"
                      permissionIdentifier="NoSensitizerRequired"
                    >
                      {({ pageName, moduleName, permissionIdentifier }) => (
                        <button
                          type="button"
                          className="btn btn-sm fw-500 text-white"
                          style={{ backgroundColor: NO_SENSitizer_BUTTON_COLOR, borderColor: NO_SENSitizer_BUTTON_COLOR }}
                          disabled={isNoSensitizerSaving}
                          onClick={handleNoSensitizerRequired}
                        >
                          {t(getPermissionDisplayName(pageName, moduleName, permissionIdentifier, "No Sensitizer Required"))}
                        </button>
                      )}
                    </PermissionComponent>

                    {sentivityResultData.length > 0 && (
                      <button
                        type="button"
                        className="btn btn-sm fw-bold text-white"
                        style={{
                          backgroundColor: ADD_NEW_ANTIMICROBIAL_BUTTON_COLOR,
                          borderColor: ADD_NEW_ANTIMICROBIAL_BUTTON_COLOR,
                        }}
                        disabled={isLoadingSensitivity}
                        onClick={() => {
                          const lastPanel = sentivityResultData[sentivityResultData.length - 1];
                          loadSensitivityPanel(
                            toNumber(lastPanel.panelId),
                            lastPanel.panelName || "",
                            true
                          );
                        }}
                      >
                        {t("Add New Antimicrobial")}
                      </button>
                    )}
                  </div>
                  <div className="separator border-gray-200 mx-4 mb-4" />
                </>
              )
            }
            <div className="d-flex flex-wrap gap-2 mt-4 mb-4">
              {isShowHistoricData && (
                <button
                  id={`MicrobiologyResultDataExpandShowHistoricalData`}
                  className="btn btn-sm fw-bold text-white"
                  style={{ background: "#6ab8e2" }}
                  onClick={() => setShowViewHistoricalData(true)}
                >
                  {t("Show Historical Data")}
                </button>
              )}
              {(activeSortOrder === 2 || activeSortOrder === 3 || activeSortOrder === 4 || activeSortOrder === 5) && (
                <PermissionComponent
                  moduleName="Microbiology LIS"
                  pageName="Result Data"
                  permissionIdentifier="Preview"
                >
                  {({ pageName, moduleName, permissionIdentifier }) => (
                    <button
                      type="button"
                      className="btn btn-sm fw-bold text-white"
                      style={{ background: "#ff9800" }}
                      disabled={isPreviewing}
                      onClick={handleMicrobiologyPreview}
                    >
                      {isPreviewing
                        ? t("Previewing...")
                        : t(getPermissionDisplayName(pageName, moduleName, permissionIdentifier, "Preview"))}
                    </button>
                  )}
                </PermissionComponent>
              )}
              {!isCompletedTab && (
                <PermissionComponent
                  moduleName="Microbiology LIS"
                  pageName="Result Data"
                  permissionIdentifier="Prelim"
                >
                  {({ pageName, moduleName, permissionIdentifier }) => (
                    <button
                      type="button"
                      id={`MicrobiologyResultDataExpandPrelim`}
                      className="btn btn-sm fw-bold text-white"
                      style={{ background: "#0a66c2" }}
                      disabled={isApplyingPrelim || isUpdatingLisStatus}
                      onClick={handlePrelim}
                    >
                      {isApplyingPrelim
                        ? t("Applying...")
                        : t(getPermissionDisplayName(pageName, moduleName, permissionIdentifier, "Prelim"))}
                    </button>
                  )}
                </PermissionComponent>
              )}
              <PermissionComponent
                moduleName="Microbiology LIS"
                pageName="Result Data"
                permissionIdentifier="Publish&Validate"
              >
                {({ pageName, moduleName, permissionIdentifier }) => (
                  <button
                    type="button"
                    className="btn btn-sm fw-bold text-white"
                    style={{ background: "#dc3545" }}
                    disabled={isUpdatingLisStatus || !canPublishAndValidate}
                    title={
                      canPublishAndValidate
                        ? undefined
                        : t("Available only when the status is Ready to Publish.")
                    }
                    onClick={handlePublishAndValidate}
                  >
                    {isUpdatingLisStatus && updatingLisStatusAction === "publish"
                      ? t("Publishing...")
                      : activeSortOrder === 5
                        ? "Re-Publish & Validate"
                        : "Publish & Validate"}
                  </button>
                )}
              </PermissionComponent>
              <PermissionComponent
                moduleName="Microbiology LIS"
                pageName="Result Data"
                permissionIdentifier="Save"
              >
                {({ pageName, moduleName, permissionIdentifier }) => (
                  <button
                    id={`MicrobiologyResultDataExpandSave`}
                    className="btn btn-sm fw-bold text-white"
                    style={{ background: "#28a745" }}
                    disabled={isSubmitting}
                    onClick={() => saveMicrobiologyExpandResultData()}
                  >
                    {isSubmitting ? t("Saving") : t(getPermissionDisplayName(pageName, moduleName, permissionIdentifier, "Save"))}
                  </button>
                )}
              </PermissionComponent>
              {isShowCompleteButton && (
                <PermissionComponent
                  moduleName="Microbiology LIS"
                  pageName="Result Data"
                  permissionIdentifier="Complete"
                >
                  {({ pageName, moduleName, permissionIdentifier }) => (
                    <button
                      type="button"
                      className="btn btn-sm fw-bold text-white"
                      style={{ background: "#2196f3" }}
                      disabled={isUpdatingLisStatus}
                      onClick={async () => {
                        const tabId = filterData?.tabId ?? 0;
                        const completeLabel = getCompleteLabelBySortOrder(activeSortOrder);
                        setIsUpdatingLisStatus(true);
                        setUpdatingLisStatusAction("complete");
                        try {
                          const res: AxiosResponse = await UpdateLisStatus({
                            reqOrderId: requisitionOrderId,
                            tabId,
                          });
                          if (res?.data?.statusCode === 200) {
                            toast.success(t(res?.data?.message || completeLabel));
                            loadGridData(false);
                          } else {
                            toast.error(t(res?.data?.message || `Failed to ${completeLabel.toLowerCase()}`));
                          }
                        } catch (err: any) {
                          toast.error(t(`Error ${completeLabel.toLowerCase()}`));
                        } finally {
                          setIsUpdatingLisStatus(false);
                          setUpdatingLisStatusAction(null);
                        }
                      }}
                    >
                      {isUpdatingLisStatus && updatingLisStatusAction === "complete"
                        ? t("Completing...")
                        : t(getPermissionDisplayName(pageName, moduleName, permissionIdentifier, getCompleteLabelBySortOrder(activeSortOrder)))}
                    </button>
                  )}
                </PermissionComponent>
              )}
              {activeSortOrder === 5 && (
                <PermissionComponent
                  moduleName="Microbiology LIS"
                  pageName="Result Data"
                  permissionIdentifier="Reject"
                >
                  {({ pageName, moduleName, permissionIdentifier }) => (
                    <button
                      type="button"
                      className="btn btn-sm fw-bold text-white"
                      style={{ background: "#e90e24" }}
                      onClick={() => setShowRejectModal(true)}
                    >
                      {t(getPermissionDisplayName(pageName, moduleName, permissionIdentifier, "Reject"))}
                    </button>
                  )}
                </PermissionComponent>
              )}
            </div>
          </div>
        )}
      </div>

      <ViewHistoricalDataModal
        show={showViewHistoricalData}
        onHide={() => setShowViewHistoricalData(false)}
        reqOrderId={requisitionOrderId}
        tabId={filterData?.tabId}
        lookupOptions={{
          colonyCounts: colonyCountOptions,
          cultureGrowths: cultureGrowthOptions,
          gramStains: gramStainOptions,
          sources: sourceOptions,
          organisms: organismOptions,
          cannedComments: cannedCommentOptions,
        }}
      />

      <RejectModal
        show={showRejectModal}
        onHide={() => setShowRejectModal(false)}
        requisitionOrderId={requisitionOrderId}
        requisitionId={requisitionId}
        onSuccess={() => {
          loadGridData(false);
        }}
      />

      <DeleteSensitivityModal
        show={!!panelToDelete}
        panelName={panelToDelete?.panelName}
        isDeleting={isDeletingSensitivity}
        onHide={() => {
          if (!isDeletingSensitivity) {
            setPanelToDelete(null);
          }
        }}
        onConfirm={handleConfirmDeleteSensitivity}
      />
    </div>
  );
};

export default ResultDataExpandableRow;
